// Live HTML preview of the Order Receipt — follows the same clean style as the
// invoice template (no coloured band / no background), titled ORDER RECEIPT,
// with bank + UPI + terms below the totals. Mirrors the receipt PDF.
// Uses fixed sample line/client/totals; the editable config drives the rest.
import { QRCodeSVG } from "qrcode.react";

export interface ReceiptPreviewConfig {
  logoUrl?: string | null;
  businessName: string;
  gstin: string;
  address: string;
  upi: string;
  bankAccountName: string;
  bankAccountNumber: string;
  bankIfsc: string;
  bankName: string;
  bankBranch: string;
  terms: string[];
  notes?: string | null;
}

function rs(n: number) {
  return `Rs. ${new Intl.NumberFormat("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n)}`;
}

export function ReceiptHtmlPreview({ config }: { config: ReceiptPreviewConfig }) {
  const subtotal = 5000;
  const cgst = 450;
  const sgst = 450;
  const total = subtotal + cgst + sgst;
  const received = 0;
  const pending = total - received;

  // Sample values for the dynamic placeholders (the real receipt fills live data).
  const sub = (s: string) =>
    s
      .replace(/\{\{\s*Due Date \+ 7\s*\}\}/gi, "2026-06-29")
      .replace(/\{\{\s*Due Date\s*\}\}/gi, "2026-06-22")
      .replace(/\{\{\s*Customer\.?\s*Account Name\s*\}\}/gi, "Acme Co.")
      .replace(/\{\{\s*Customer\.?\s*GST\s*Number\s*\}\}/gi, "")
      .replace(/\{\{\s*Customer\.?\s*Primary Name\s*\}\}/gi, "Rajesh Kumar")
      .replace(/\{\{\s*Customer\.?\s*Primary Phone Number\s*\}\}/gi, "+91 99999 00001")
      .replace(/\{\{\s*Customer\.?\s*Company Address\s*\}\}/gi, "2nd Floor, Sample Tower, Delhi");
  const DEFAULT_TERMS = [
    "Rent shall be payable on or before {{Due Date}}. Late payments beyond {{Due Date + 7}} shall attract a penalty of Rs. 2,000 or 5% of the monthly rent (whichever is higher).",
    "If payment remains outstanding beyond 15 days from the due date, Hoblix Space reserves the right to terminate the agreement and reassign the cabin without further notice.",
  ];
  const customTerms = config.terms.filter((t) => t && t.trim());
  const terms = (customTerms.length > 0 ? customTerms : DEFAULT_TERMS).map(sub);

  return (
    <div className="bg-white p-8 text-[13px] text-zinc-900">
      {config.logoUrl ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img src={config.logoUrl} alt="logo" className="mb-3 h-20 object-contain" />
      ) : null}

      {/* Header */}
      <div className="mb-4 flex items-start justify-between border-b pb-3">
        <div>
          <div className="text-lg font-bold">{config.businessName || "Business name"}</div>
          {config.address ? (
            <div className="whitespace-pre-line text-xs">{config.address}</div>
          ) : null}
          <div className="text-xs">Delhi (07)</div>
          {config.gstin ? (
            <div className="text-xs">
              GSTIN: <span className="font-mono">{config.gstin}</span>
            </div>
          ) : null}
        </div>
        <div className="text-right">
          <div className="text-lg font-bold">ORDER RECEIPT</div>
          <div className="mt-1 font-mono text-base">#05/062026</div>
          <div className="text-xs">Date: 2026-06-15</div>
          <div className="text-xs">Due: 2026-06-22</div>
        </div>
      </div>

      {/* Bill to — customer details */}
      <div className="mb-4">
        <div className="text-xs font-bold text-zinc-500">BILL TO:</div>
        {/* Firm: contact person on top, firm addressed as "M/s {name}". */}
        <div className="text-xs">Rajesh Kumar</div>
        <div>M/s Acme Co.</div>
        <div className="text-xs">2nd Floor, Sample Tower, Delhi</div>
        <div className="text-xs">GSTIN: 07AAACA1234A1Z5</div>
        <div className="text-xs">Ph: +91 99999 00001</div>
      </div>

      {/* Line table — invoice style */}
      <table className="w-full border-collapse text-[12px]">
        <thead>
          <tr className="border-y text-left text-[11px] text-zinc-500">
            <th className="py-1.5 font-semibold">DESCRIPTION</th>
            <th className="py-1.5 text-center font-semibold">QTY</th>
            <th className="py-1.5 text-right font-semibold">RATE</th>
            <th className="py-1.5 text-right font-semibold">TAXABLE</th>
            <th className="py-1.5 text-center font-semibold">GST</th>
            <th className="py-1.5 text-right font-semibold">LINE TOTAL</th>
          </tr>
        </thead>
        <tbody>
          <tr className="border-b">
            <td className="py-2">Plan Rental — Workstation</td>
            <td className="py-2 text-center">1</td>
            <td className="py-2 text-right font-mono">{rs(subtotal)}</td>
            <td className="py-2 text-right font-mono">{rs(subtotal)}</td>
            <td className="py-2 text-center">18%</td>
            <td className="py-2 text-right font-mono">{rs(total)}</td>
          </tr>
        </tbody>
      </table>

      {/* Totals */}
      <div className="mt-3 flex justify-end">
        <div className="w-[280px] text-[12px]">
          <div className="flex justify-between py-1">
            <span className="text-zinc-500">Subtotal (excl. GST):</span>
            <span className="font-mono">{rs(subtotal)}</span>
          </div>
          <div className="flex justify-between py-1">
            <span className="text-zinc-500">CGST:</span>
            <span className="font-mono">{rs(cgst)}</span>
          </div>
          <div className="flex justify-between py-1">
            <span className="text-zinc-500">SGST:</span>
            <span className="font-mono">{rs(sgst)}</span>
          </div>
          <div className="flex justify-between border-t py-1.5 font-bold">
            <span>TOTAL:</span>
            <span className="font-mono">{rs(total)}</span>
          </div>
          <div className="flex justify-between py-1">
            <span className="text-zinc-500">Received:</span>
            <span className="font-mono text-green-600">{rs(received)}</span>
          </div>
          <div className="flex justify-between border-t py-1.5 font-bold">
            <span>TOTAL TO BE PAID:</span>
            <span className="font-mono">{rs(pending)}</span>
          </div>
        </div>
      </div>

      {/* Payment info — bank + UPI */}
      <div className="mt-6 grid grid-cols-2 gap-4 border-t pt-4 text-[12px]">
        <div>
          <div className="mb-1 text-[11px] font-bold text-zinc-500">BANK TRANSFER</div>
          {[
            ["Account name", config.bankAccountName],
            ["Account no.", config.bankAccountNumber],
            ["IFSC", config.bankIfsc],
            ["Bank", config.bankName],
            ["Branch", config.bankBranch],
          ].map(([k, v]) => (
            <div key={k} className="flex justify-between py-0.5">
              <span className="text-zinc-500">{k}</span>
              <span className="font-mono">{v || "—"}</span>
            </div>
          ))}
        </div>
        <div>
          <div className="mb-1 text-[11px] font-bold text-zinc-500">PAY BY UPI</div>
          <div className="font-mono">{config.upi || "—"}</div>
          {config.upi.trim() ? (
            <div className="mt-2 rounded border bg-white p-1.5">
              <QRCodeSVG
                value={`upi://pay?pa=${encodeURIComponent(config.upi.trim())}&pn=${encodeURIComponent(
                  config.businessName || "Hoblix",
                )}&cu=INR`}
                size={76}
              />
            </div>
          ) : (
            <div className="mt-2 flex h-[88px] w-[88px] items-center justify-center rounded border text-center text-[9px] text-zinc-400">
              Add UPI ID
            </div>
          )}
        </div>
      </div>

      {/* Terms */}
      {terms.length > 0 ? (
        <div className="mt-5 border-t pt-4 text-[12px]">
          <div className="mb-2 text-[11px] font-bold text-zinc-500">TERMS &amp; CONDITIONS</div>
          <ol className="list-decimal space-y-1 pl-5 text-zinc-700">
            {terms.map((t, i) => (
              <li key={i}>{t}</li>
            ))}
          </ol>
        </div>
      ) : (
        <div className="mt-5 border-t pt-4 text-[11px] text-zinc-400">
          Built-in default terms will be used (add terms to override).
        </div>
      )}

      {config.notes && config.notes.trim() ? (
        <div className="mt-3 text-xs italic text-zinc-600">{sub(config.notes)}</div>
      ) : null}

      <div className="mt-6 border-t pt-2 text-xs text-zinc-500">
        This is a computer-generated order receipt issued before payment. Amount above is payable
        as per the agreed terms.
      </div>
    </div>
  );
}
